import exifr from 'exifr';
import { ParseMakerNote } from './ParseMakerNote.js';

const MAX_BYTES_TO_PARSE = 60000;

const ROTATION_LOOKUP = { 1: 0, 6: 1, 3: 2, 8: 3 };

const EXIF_TAGS = {
  date_taken: 'DateTimeOriginal',
  camera_make: 'Make',
  camera_model: 'Model',
  lens: 'LensModel',
  focal_length: 'FocalLength',
  focal_length_35mm: 'FocalLengthIn35mmFormat',
  image_height: 'ExifImageHeight',
  image_width: 'ExifImageWidth',
  orientation: 'Orientation',
  iso: 'ISO',
  shutter_speed: 'ExposureTime',
  f_number: 'FNumber',
  raw_latitude: 'GPSLatitude',
  latitude_ref: 'GPSLatitudeRef',
  latitude: 'latitude', // special exifr tag that auto calcs the decimal value
  raw_longitude: 'GPSLongitude',
  longitude_ref: 'GPSLongitudeRef',
  longitude: 'longitude', // special exifr tag that auto calcs the decimal value
  direction: 'GPSImgDirection',
  direction_ref: 'GPSImgDirectionRef',
  altitude: 'GPSAltitude',
  altitude_ref: 'GPSAltitudeRef',
  camera_serial_number: 'SerialNumber',
  image_software: 'Software'
};

const XMP_TAGS = {
  use_panorama_viewer: 'UsePanoramaViewer',
  projection_type: 'ProjectionType'
};

/**
 * Converts a byte array to a binary string.
 * @param {Uint8Array} bytes - The byte array to convert to a binary string.
 * @returns {String} The binary string.
 */
function BytesToBinaryString(bytes) {
  const byteLength = bytes.byteLength;
  let binaryString = '';
  for (let i = 0; i < byteLength; i++) {
    binaryString += String.fromCharCode(bytes[i]);
  }
  return binaryString;
}

/**
 * @param {File} file
 * @returns {Promise<Uint8Array>} The byte array of the file.
 */
async function GetBytesFromFile(file) {
  const arrayBuffer = await file.arrayBuffer();
  return new Uint8Array(arrayBuffer);
}

/**
 * Parses the exif of an image from its bytes.
 * @param {File} file
 * @returns {Promise<Object>} The exif data from the file.
 */
export async function GetExif(file) {
  if (file?.type !== 'image/jpeg') throw new Error('File type must be JPEG');

  const bytes = await GetBytesFromFile(file);

  const ALL_TAGS = { ...EXIF_TAGS, ...XMP_TAGS };

  // Create object to store resulting exif.
  let res = {};

  // get the exif from the photo
  const parseOptions = {
    translateValues: false,
    xmp: true
  };
  let exif = await exifr.parse(bytes, parseOptions);

  // get MakerNote data from the photo
  let makerNoteTags = {
    0x4015: 'VignettingCorr',
    0x4016: 'VignettingCorr2'
  };

  const binaryString = BytesToBinaryString(bytes.slice(0, MAX_BYTES_TO_PARSE));
  let makerNoteTagData = ParseMakerNote(binaryString, makerNoteTags);
  if (makerNoteTagData) {
    // add the additional MakerNote tags to our allTags and exif objects
    ALL_TAGS['vignetting_correction'] = 'VignettingCorr';
    ALL_TAGS['vignetting_correction_2'] = 'VignettingCorr2';
    exif = { ...exif, ...makerNoteTagData };
  }

  // Iterate through the exifTags and put the exif in res.
  for (let key in ALL_TAGS) {
    let name = ALL_TAGS[key];
    let value = exif[name];

    if (value == null) continue;

    if (key === 'date_taken') {
      // Cast date to epoch seconds.
      value = Math.floor(value.getTime() / 1000);
    } else if (key === 'altitude_ref' && value.constructor === Uint8Array) {
      // get the first value in this typed array
      value = value[0];
    }

    if (value != null && !Number.isNaN(value)) {
      // if the value is an array, then check if any value is NaN and skip if so
      if (Array.isArray(value)) {
        let foundNaN = false;
        for (let i = 0; i < value.length; i++) {
          if (Number.isNaN(value[i])) {
            foundNaN = true;
            break;
          }
        }
        if (foundNaN) continue;
      }
      res[key] = value;
    }
  }
  res.orientation = res.orientation || 1;
  res.filename = file.name;
  if (res.filename == 'image.jpg') res.filename = Date.now() + '.jpg';
  res.original_size = file.size;
  // res.exif_md5 = SparkMD5.ArrayBuffer.hash(binaryString); // TODO: fix this to work and actually be a 'fingerprint' for the photo based on its exif data
  res.name_date = res.filename + '|' + res.date_taken;
  res.rotation = ROTATION_LOOKUP[res.orientation];
  if ((res.rotation === 1 || res.rotation === 3) && res.image_width && res.image_height) {
    let t = res.image_width;
    res.image_width = res.image_height;
    res.image_height = t;
  }
  if (exif.Software === 'Ladybug SDK') {
    res.panorama = true;
  }

  return res;
}
